import styled from "@emotion/styled"
import { debounce, get } from "lodash"
import React, { useEffect, useRef, useState, useContext } from "react"
import tw from "twin.macro"
import linkTransformer from "../utils/linkTransformer"
import triggerResizeEvent from "../utils/triggerResizeEvent"
import Button from "./Button"
import Link from "./Link"
import Picture from "./Picture"
import ThemeContext from "../context/theme-context"

const CardsGrid = styled.div`
  ${tw`flex flex-wrap justify-center -ml-4 -mt-4`}
`

const CardsColumn = styled.div`
  ${tw`pl-4 mt-4 max-w-full sm:w-1/2 md:w-1/3 xl:w-1/4`}
  width: 316px;
`

const CardsBlock = ({ block }) => {
  const cards = get(block, "elements.cards.value")
  const flippable = get(block, "elements.flippable.value[0].codename")
  const cardsGridElement = useRef(null)
  const [matchedHeight, setMatchedHeight] = useState(0)

  useEffect(() => {
    const updateHeights = () => {
      if (cardsGridElement.current) {
        setMatchedHeight(0)
        const heights = []
        const matchedNodes = cardsGridElement.current.querySelectorAll(
          ".js-match-height"
        )

        for (var i = 0; i < matchedNodes.length; i++) {
          heights.push(matchedNodes[i].offsetHeight)
        }

        const maxHeight = Math.max(...heights)

        setMatchedHeight(maxHeight ? `${maxHeight}px` : `auto`)
      }
    }

    updateHeights()

    window.addEventListener("resize", debounce(updateHeights, 150))
  }, [])

  return (
    <CardsGrid ref={cardsGridElement}>
      {cards.map((card) => {
        const id = get(card, "system.id")
        return (
          <CardsColumn key={id}>
            <Card
              card={card}
              matchedHeight={matchedHeight}
              flippable={flippable}
            />
          </CardsColumn>
        )
      })}
    </CardsGrid>
  )
}

export default CardsBlock

const StyledCardFront = styled.div`
  ${tw`bg-blue text-center w-full min-h-full`}
  ${(props) => props.hoverEffect && tw`hover:bg-black`}
  ${(props) =>
    props.hoverEffect && {
      transition: "150ms ease-in-out transform",
    }}
  &:hover, &:focus {
    ${(props) =>
      props.hoverEffect && {
        transform: "scale(1.05)",
      }}
  }
`
const StyledCardBack = styled.div`
  ${tw`flex items-center text-center w-full min-h-full bg-black`}
`

const CardInner = styled.div`
  ${tw`p-8 w-full`}
`

const FlipCardGroup = styled.div`
  perspective: 1000px;
  width: 100%;
  ${(props) =>
    props.matchedHeight && {
      height: props.matchedHeight,
    }}
`

const FlipCardWrapper = styled.div`
  transition: 0.6s;
  transform-style: preserve-3d;
  position: relative;
  height: 100%;
  cursor: pointer;
`

const FlipCard = styled.div`
  backface-visibility: hidden;
  transition: 0.6s;
  transform-style: preserve-3d;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`

const FlipCardFront = styled(FlipCard)`
  transform: rotateY(0deg);
  background: lightgreen;
  z-index: 2;
  @media (hover: hover) {
    ${FlipCardWrapper}:hover > & {
      transform: rotateY(180deg);
    }
  }
  ${FlipCardWrapper}.is-flipped > & {
    transform: rotateY(180deg);
  }
`

const FlipCardBack = styled(FlipCard)`
  ${tw`text-white flex items-center`}
  ${CardInner} {
    ${tw`relative z-10`}
  }
  transform: rotateY(-180deg);
  ${FlipCardWrapper}.is-flipped > & {
    transform: rotateY(0);
  }
  @media (hover: hover) {
    ${FlipCardWrapper}:hover > & {
      transform: rotateY(0);
    }
  }
`

const BackgroundImage = styled(Picture)`
  ${tw`object-cover inset-0 w-full h-full absolute`}
`

const Heading = styled.h3`
  ${tw`uppercase font-bold text-lg leading-tight lg:text-2xl`}
`

const Text = styled.p`
  ${tw`leading-loose text-xs mt-2 lg:text-sm`}
  & + a {
    ${tw`mt-4`}
  }
`

const Card = ({ card, flippable, matchedHeight }) => {
  const [isFlipped, setIsFlipped] = useState(false)
  const handleClick = () => setIsFlipped(!isFlipped)
  const handleKeyDown = (event) => {
    // Don’t prevent link from working
    if (event.target.localName === "a") {
      return
    }
    // Check to see if space or enter were pressed
    if (
      event.key === " " ||
      event.key === "Enter" ||
      event.key === "Spacebar"
    ) {
      // "Spacebar" for IE11 support
      // Prevent the default action to stop scrolling when space is pressed
      event.preventDefault()
      setIsFlipped(!isFlipped)
    }
  }

  if (flippable === "yes") {
    return (
      <FlipCardGroup matchedHeight={matchedHeight}>
        <FlipCardWrapper
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          className={isFlipped ? `is-flipped` : ``}
          tabIndex={0}
          role="button"
          aria-label={`Details for ${get(card, "elements.heading.value")}`}
          aria-expanded={isFlipped}
        >
          <FlipCardFront>
            <CardFront
              card={card}
              showButton={false}
              className="js-match-height"
            />
          </FlipCardFront>
          <FlipCardBack aria-hidden={!isFlipped}>
            <CardBack
              card={card}
              className="js-match-height"
              isFlipped={isFlipped}
            />
          </FlipCardBack>
        </FlipCardWrapper>
      </FlipCardGroup>
    )
  } else {
    return <CardFront card={card} showButton={true} hoverEffect={true} />
  }
}

const CardFront = ({ card, showButton, ...props }) => {
  const image = get(
    card,
    "elements.image.value[0].elements.image.value[0]"
  )
  const imageAltText = get(
    card,
    "elements.image.value[0].elements.alt_text.value"
  )
  const logo = get(
    card,
    "elements.logo.value[0].elements.image.value[0]"
  )
  const logoAltText = get(
    card,
    "elements.logo.value[0].elements.alt_text.value"
  )

  const transformedLink = linkTransformer(card)
  const { hydrated: isClient } = useContext(ThemeContext)

  const handleImageLoad = () => {
    triggerResizeEvent()
  }

  return (
    <StyledCardFront {...props}>
      <Picture
        image={image}
        alt={imageAltText}
        onLoad={handleImageLoad}
        cropRatio={0.5625}
        cropWidth={375}
      />
      <CardInner>
        {/* eslint-disable */}
        <img
          src={logo.url}
          alt={logoAltText}
          onLoad={handleImageLoad}
          className={`${isClient ? 'lazyload' : ''}`}
        />{" "}
        {/* eslint-enable */}
        {showButton && transformedLink.label ? (
          <Button
            contrastText={true}
            type="underline"
            to={transformedLink.to}
            target={transformedLink.openInNewWindow ? "_blank" : null}
            as={Link}
          >
            {transformedLink.label}
          </Button>
        ) : null}
      </CardInner>
    </StyledCardFront>
  )
}

const CardBack = ({ card, isFlipped, ...props }) => {
  const heading = get(card, "elements.heading.value")
  const text = get(card, "elements.text.value")
  const backgroundImage = get(
    card,
    "elements.background_image.value[0].elements.image.value[0]"
  )
  const backgroundImageAltText = get(
    card,
    "elements.background_image.value[0].elements.alt_text.value"
  )

  const transformedLink = linkTransformer(card)

  return (
    <StyledCardBack {...props}>
      <CardInner>
        <Heading>{heading}</Heading>
        <Text dangerouslySetInnerHTML={{ __html: text }} />
        {transformedLink.label && (
          <Button
            contrastText={true}
            type="underline"
            to={transformedLink.to}
            target={transformedLink.openInNewWindow ? "_blank" : null}
            tabIndex={isFlipped ? `0` : `-1`}
            as={Link}
          >
            {transformedLink.label}
          </Button>
        )}
      </CardInner>
      {backgroundImage ? (
        <BackgroundImage
          image={backgroundImage}
          cropWidth={400}
          cropRatio={1.25}
          cover={true}
          alt={backgroundImageAltText}
        />
      ) : null}
    </StyledCardBack>
  )
}
